import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { GET_PAGINATED_ROOMS_BY_TYPE as PaginatedRoomsByType } from '../graphql/types/GET_PAGINATED_ROOMS_BY_TYPE';
import { useQuery } from '@apollo/client';
import { GET_PAGINATED_ROOMS_BY_TYPE } from '../graphql/GET_PAGINATED_ROOMS_BY_TYPE';

export type Room = PaginatedRoomsByType['getPaginatedRoomsByType'][number];

export enum ChatTabType {
  ALL = 'all',
  COFFEE = 'coffee'
}

interface TabbedRoomListsContextValues {
  rooms: Room[];
  error: unknown;
  loading: boolean;
  handleFetchNextRoomPage: () => Promise<void>;
  hasMoreRoomsToFetch: boolean;
  isFetchingMoreRooms: boolean;
  roomsPerPage: number;
  currentPage: number;
  activeTab: ChatTabType;
  setActiveTab: (tab: ChatTabType) => void;
  coffeeChatsCount: number;
}

const TabbedRoomListsContext = createContext<TabbedRoomListsContextValues | null>(null);

const INITIAL_PAGE = 1;
const ROOMS_LIMIT = 20;

/**
 * useContext hook for the getting paginated room lists with tabs
 * Make sure to wrap the components with `TabbedRoomListsContextProvider` before using this hook
 */
export const useTabbedRoomListsContext = () => {
  const currentTabbedRoomListsContext = useContext(TabbedRoomListsContext);
  if (!currentTabbedRoomListsContext) {
    throw new Error(
      'useTabbedRoomListsContext has to be used within <TabbedRoomListsContextProvider>'
    );
  }
  return currentTabbedRoomListsContext;
};

const TabbedRoomListsContextProvider = (props: { children: React.ReactNode }) => {
  const [activeTab, setActiveTab] = useState<ChatTabType>(ChatTabType.ALL);
  const [currentRoomsPage, setCurrentRoomsPage] = useState(INITIAL_PAGE);
  const [hasMoreRoomsToFetch, setHasMoreRoomsToFetch] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Determine chatType based on active tab - memoized to prevent unnecessary re-renders
  const chatType = useMemo(() =>
    activeTab === ChatTabType.COFFEE ? 'coffee' : null,
    [activeTab]
  );

  const { loading, error, data, fetchMore, refetch } = useQuery(GET_PAGINATED_ROOMS_BY_TYPE, {
    variables: {
      limit: ROOMS_LIMIT,
      page: INITIAL_PAGE,
      chatType
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    onCompleted: (data) => {
      setIsInitialLoad(false);
      const roomsData = data?.getPaginatedRoomsByType as Room[] | undefined;
      const totalRoomsLength = roomsData?.length ?? 0;
      if (!totalRoomsLength || totalRoomsLength < ROOMS_LIMIT) {
        setHasMoreRoomsToFetch(false);
      }
    },
    onError: (error) => {
      console.error('Error fetching rooms:', error);
      setIsInitialLoad(false);
      setHasMoreRoomsToFetch(false);
    }
  });

  // Reset pagination and refetch when tab changes
  useEffect(() => {
    // Skip the initial load since the query will handle it
    if (isInitialLoad) {
      return;
    }

    setCurrentRoomsPage(INITIAL_PAGE);
    setHasMoreRoomsToFetch(true);
    refetch({
      limit: ROOMS_LIMIT,
      page: INITIAL_PAGE,
      chatType
    }).catch((error) => {
      console.error('Error refetching rooms:', error);
    });
  }, [chatType, isInitialLoad, refetch]);

  const handleFetchNextRoomPage = useCallback(async () => {
    if (!hasMoreRoomsToFetch) return;
    const nextPage = currentRoomsPage + 1;

    await fetchMore({
      updateQuery: (prevCache, { fetchMoreResult }) => {
        const newRoomsData = fetchMoreResult?.getPaginatedRoomsByType as
          | Room[]
          | undefined;
        if (!newRoomsData || newRoomsData.length < ROOMS_LIMIT) {
          setHasMoreRoomsToFetch(false);
        }
        if (!newRoomsData?.length) {
          return prevCache;
        }
        return {
          ...prevCache,
          getPaginatedRoomsByType: [...prevCache.getPaginatedRoomsByType, ...newRoomsData]
        };
      },
      variables: {
        limit: ROOMS_LIMIT,
        page: nextPage,
        chatType
      }
    });
    setCurrentRoomsPage(nextPage);
  }, [hasMoreRoomsToFetch, currentRoomsPage, fetchMore, chatType]);

  // Calculate coffee chats count - memoized to prevent unnecessary recalculations
  const allRooms = useMemo(() =>
    (data?.getPaginatedRoomsByType ?? []) as Room[],
    [data?.getPaginatedRoomsByType]
  );

  const coffeeChatsCount = useMemo(() => {
    if (activeTab === ChatTabType.COFFEE) {
      return allRooms.length;
    }
    return allRooms.filter(room => room.chatType === 'coffee').length;
  }, [allRooms, activeTab]);

  const values: TabbedRoomListsContextValues = useMemo(() => ({
    rooms: allRooms,
    error,
    loading,
    handleFetchNextRoomPage,
    hasMoreRoomsToFetch,
    isFetchingMoreRooms: loading,
    currentPage: currentRoomsPage,
    roomsPerPage: ROOMS_LIMIT,
    activeTab,
    setActiveTab,
    coffeeChatsCount
  }), [
    allRooms,
    error,
    loading,
    handleFetchNextRoomPage,
    hasMoreRoomsToFetch,
    currentRoomsPage,
    activeTab,
    coffeeChatsCount
  ]);

  return (
    <TabbedRoomListsContext.Provider value={values}>
      {props.children}
    </TabbedRoomListsContext.Provider>
  );
};

export default TabbedRoomListsContextProvider;